import { audio } from '@/audio-singleton';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { gameSignals } from '@/bridge/gameSignals';
import { t } from '@/i18n';
import { Header } from './Header';
import { Toasts } from './Toasts';
import { ShareModal } from './ShareModal';
import { SettingsModal } from './SettingsModal';
import { StatsOverlay } from './StatsOverlay';
import { ConfirmDialog } from './ConfirmDialog';
import { ResultSummary } from './ResultSummary';
import { ArchetypeCallout } from './ArchetypeCallout';
import { CountdownCard } from './CountdownCard';
import { StatsIconBadge } from './StatsIconBadge';
import { PracticeBanner } from './PracticeBanner';
import { uiCommandBus } from '@/bridge/uiCommandBus';
import { HintAffordance } from './HintAffordance';
import { PersonalStatsHeaderButton } from './PersonalStatsHeaderButton';
import { PersonalStatsOverlay } from './PersonalStatsOverlay';
import { ErrorBoundary } from './ErrorBoundary';
import { ChromeFallback } from './ChromeFallback';
import { D1StatsProvider } from '@/stats/D1StatsProvider';
import { createLocalStateStore } from '@/lib/local-state';
import { deriveShareMarker } from '@/lib/share-grid';
import type { UiPrefs } from '@/lib/ui-prefs';
import { pushToast } from '@/notifications';
import { ShareIcon } from './icons';
import type { Zone } from '@/lib/zones';
import type { ShotResult } from '@/lib/local-state';

export interface AppProps {
  uiPrefs: UiPrefs;
}

const provider = new D1StatsProvider();

function observationsFromShots(shots: readonly ShotResult[]): Record<Zone, number> {
  const out: Record<Zone, number> = { LF: 0, CF: 0, RF: 0, LA: 0, CA: 0, RA: 0 };
  for (const s of shots) out[s.keeperZone]++;
  return out;
}

export function App({ uiPrefs }: AppProps) {
  const [shareOpen, setShareOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [personalStatsOpen, setPersonalStatsOpen] = useState(false);
  const autoStatsFired = useRef(false);
  const localStore = useMemo(() => createLocalStateStore(window.localStorage), []);

  const scene = gameSignals.currentScene.value;
  const dateKey = gameSignals.dateKey.value;
  const gameMode = gameSignals.gameMode.value;

  useEffect(() => {
    if (scene !== 'RESULT' || !dateKey) return;
    const ctl = new AbortController();
    provider.getDailyStats(dateKey, ctl.signal)
      .then((d) => {
        if (ctl.signal.aborted) return;
        gameSignals.todayAverage.value = d?.todayAverage ?? null;
        gameSignals.todayPlayerCount.value = d?.todayPlayerCount ?? null;
        // Auto-open stats overlay only when community data is sufficient AND
        // the personal-stats overlay is not already open (Plan #6 §5.5
        // precedence rule).
        if (!autoStatsFired.current && !personalStatsOpen && (d?.todayPlayerCount ?? 0) >= 20) {
          autoStatsFired.current = true;
          setStatsOpen(true);
        }
      })
      .catch((err) => {
        if (ctl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Silent failure per spec §4.4: community line stays hidden on fetch error.
      });
    return () => ctl.abort();
  }, [scene, dateKey, personalStatsOpen]);

  // Plan #6 §5.5 — opening personal stats dismisses the community overlay
  // if it was already auto-open, and prevents the community auto-open from
  // firing again this session.
  useEffect(() => {
    if (!personalStatsOpen) return;
    if (statsOpen) setStatsOpen(false);
    autoStatsFired.current = true;
  }, [personalStatsOpen]);

  // §4.2 share-leak belt-and-suspenders: if a practice round starts while the
  // share sheet is open, force it closed (buildShareText already reads `record`,
  // never `practiceRecord` — this just closes the now-stale sheet).
  useEffect(() => {
    if (gameMode === 'practice' && shareOpen) setShareOpen(false);
  }, [gameMode, shareOpen]);

  function performReset() {
    try {
      localStore.resetAll();
      uiPrefs.resetAll();
      window.location.reload();
    } catch {
      pushToast(t('reset.failed'), 'error');
      setConfirmResetOpen(false);
    }
  }

  return (
    <ErrorBoundary fallback={<ChromeFallback />}>
      <Header
        onOpenSettings={() => setSettingsOpen(true)}
        rightSlot={
          <PersonalStatsHeaderButton
            store={localStore}
            prefs={uiPrefs}
            onOpen={() => setPersonalStatsOpen(true)}
          />
        }
      />
      <Toasts />

      {scene === 'PLAY' && gameMode !== 'practice' && <StatsIconBadge onOpen={() => setStatsOpen(true)} prefs={uiPrefs} />}

      <PracticeBanner />
      {scene === 'PLAY' && gameSignals.gameMode.value !== 'practice' && <HintAffordance />}

      {scene === 'MENU' && !gameSignals.hideHeatmap.value && (
        <section class="absolute inset-x-0 top-[calc(env(safe-area-inset-top)+44px)] pointer-events-none">
          <p class="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-text-dim mt-4">
            {t('archetype.todaysKeeper')}
          </p>
          <ArchetypeCallout mode="preplay" />
        </section>
      )}

      {scene === 'RESULT' && (
        <main class="absolute inset-x-0 top-[calc(env(safe-area-inset-top)+44px)] bottom-0 pointer-events-auto overflow-y-auto">
          <div class="min-h-full flex flex-col items-stretch py-3">
            <ResultSummary />
            {gameSignals.record.value && deriveShareMarker(gameSignals.record.value) === '🕶️' && (
              <div
                data-testid="hard-mode-badge"
                class="mx-auto mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-bg-2 text-foil font-mono text-[10px] uppercase tracking-[0.2em]"
              >
                🕶️ {t('settings.hardMode')}
              </div>
            )}
            {gameSignals.record.value && (
              <ArchetypeCallout mode="truth" observations={observationsFromShots(gameSignals.record.value.shots)} compact />
            )}
            <div class="mx-auto mt-3 flex justify-center items-center gap-2">
              <button
                class="inline-flex items-center gap-2.5 px-5 py-2 min-h-[44px] rounded-md bg-foil text-bg font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-foil-bright active:scale-95 transition-all shadow-[0_0_20px_-4px_rgba(251,191,36,0.45)]"
                onClick={() => { audio().play('tap'); setShareOpen(true); }}
              ><ShareIcon size={16} /> {t('share.shareButton')}</button>
              <button
                class="inline-flex items-center gap-2.5 px-5 py-2 min-h-[44px] rounded-md border border-foil/60 text-foil font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-foil/10 active:scale-95 transition-all"
                onClick={() => uiCommandBus.emit({ type: 'practice:start' })}
              >{t('practice.again')}</button>
            </div>
            <CountdownCard />
          </div>
        </main>
      )}

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} />
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onRequestReset={() => setConfirmResetOpen(true)}
        prefs={uiPrefs}
      />
      <StatsOverlay open={statsOpen} onClose={() => setStatsOpen(false)} provider={provider} />
      <PersonalStatsOverlay
        open={personalStatsOpen}
        onClose={() => {
          // Write the seen-longest snapshot synchronously *before* flipping
          // open=false so the next App render reads the fresh value
          // (Plan #6 §13 catch — useEffect cleanup runs post-commit so the
          // header button would otherwise re-render with stale seen and keep
          // the dot visible).
          uiPrefs.setPersonalStatsLastSeenLongest(localStore.getStreak().longest);
          setPersonalStatsOpen(false);
        }}
        store={localStore}
        prefs={uiPrefs}
      />
      <ConfirmDialog
        open={confirmResetOpen}
        title={t('settings.resetConfirmTitle')}
        body={t('settings.resetConfirmBody')}
        cancelLabel={t('settings.cancel')}
        confirmLabel={t('settings.confirm')}
        onCancel={() => setConfirmResetOpen(false)}
        onConfirm={performReset}
      />
      <a
        href="/privacy.html"
        class="absolute bottom-1 left-2 font-mono text-[9px] uppercase tracking-[0.2em] text-text-dim/60 hover:text-text-2 pointer-events-auto select-none"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        Adatvédelem
      </a>
    </ErrorBoundary>
  );
}
